import { useEffect, useState, useCallback, Fragment } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { getApiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import {
  Shield,
  Plus,
  Edit,
  Users,
  Lock,
  CheckCircle,
  XCircle,
  Eye,
  FileEdit,
  FilePlus,
  FileCheck,
  Download,
  List,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  view: Eye,
  listing: List,
  create: FilePlus,
  edit: FileEdit,
  approve: FileCheck,
  export: Download,
};

const ACTION_COLORS = {
  view: 'bg-blue-100 text-blue-600 border-blue-200',
  listing: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  create: 'bg-green-100 text-green-600 border-green-200',
  edit: 'bg-yellow-100 text-yellow-600 border-yellow-200',
  approve: 'bg-purple-100 text-purple-600 border-purple-200',
  export: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function RolesPermissions() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('roles');
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const [borrowerCompanies, setBorrowerCompanies] = useState([]);
  const [clientTags, setClientTags] = useState([]);
  
  // Dialogs
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [isEditRoleOpen, setIsEditRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Form
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    hierarchy_level: 50,
    permissions: {},
    treasury_account_ids: null,     // null = all accounts; array = specific only
    borrower_ids: null,             // null = all borrower companies; array = specific only
    ie_own_entries_only: false,     // true = data entry team sees only their own entries
    transaction_type_ids: null,     // null = all types; array = specific types only
    allowed_client_tags: null, // null = all tags; array = specific tag IDs only
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/roles`, { 
        headers: getAuthHeaders(), 
        credentials: 'include' 
      });
      if (response.ok) {
        setRoles(await response.json());
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error(error?.message || "Something went wrong. Please try again.");
    }
  }, []);

  const fetchModulesAndActions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/permissions/modules`, { 
        headers: getAuthHeaders(), 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setModules(data.modules || []);
        setActions(data.actions || []);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchModulesAndActions();
    // Fetch all treasury accounts for the selector
    const fetchTreasuryAccounts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/treasury?page_size=200`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setTreasuryAccounts(data.items || (Array.isArray(data) ? data : []));
        }
      } catch (e) { console.error('Failed to fetch treasury accounts', e); }
    };

    // Fetch all borrower companies (vendors) for the selector
    const fetchBorrowerCompanies = async () => {
      try {
        const res = await fetch(`${API_URL}/api/loans/vendors`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setBorrowerCompanies(Array.isArray(data) ? data : []);
        }
      } catch (e) { console.error('Failed to fetch borrower companies', e); }
    };

    // Fetch all transaction tags for the selector
    const fetchTransactionTags = async () => {
      try {
        const res = await fetch(`${API_URL}/api/client-tags`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setClientTags(Array.isArray(data) ? data : []);
        }
      } catch (e) { console.error('Failed to fetch transaction tags', e); }
    };

    fetchTreasuryAccounts();
    fetchBorrowerCompanies();
    fetchTransactionTags();
  }, [fetchRoles, fetchModulesAndActions]);

  const handleCreateRole = async () => {
    if (!roleForm.name || !roleForm.display_name) {
      toast.error('Name and Display Name are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(roleForm),
      });
      
      if (response.ok) {
        toast.success('Role created successfully');
        setIsAddRoleOpen(false);
        resetForm();
        fetchRoles();
      } else {
        toast.error(await getApiError(response));
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong. Please try again.");
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;
    
    try {
      const response = await fetch(`${API_URL}/api/roles/${selectedRole.role_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          hierarchy_level: roleForm.hierarchy_level,
          treasury_account_ids: roleForm.treasury_account_ids,
          borrower_ids: roleForm.borrower_ids,
          ie_own_entries_only: roleForm.ie_own_entries_only,
          transaction_type_ids: roleForm.transaction_type_ids,
          allowed_client_tags: roleForm.allowed_client_tags,
        }),
      });
      
      if (response.ok) {
        toast.success('Role updated successfully');
        setIsEditRoleOpen(false);
        fetchRoles();
      } else {
        toast.error(await getApiError(response));
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong. Please try again.");
    }
  };

  const openEditRole = (role) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name || '',
      display_name: role.display_name || '',
      description: role.description || '',
      hierarchy_level: role.hierarchy_level || 50,
      permissions: role.permissions || {},
      treasury_account_ids: role.treasury_account_ids || null,
      borrower_ids: role.borrower_ids || null,
      ie_own_entries_only: role.ie_own_entries_only || false,
      transaction_type_ids: role.transaction_type_ids || null,
      allowed_client_tags: role.allowed_client_tags || null,
    });
    setIsEditRoleOpen(true);
  };

  const resetForm = () => {
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      hierarchy_level: 50,
      permissions: {},
      treasury_account_ids: null,
      borrower_ids: null,
      ie_own_entries_only: false,
      transaction_type_ids: null,
      allowed_client_tags: null,
    });
  };

  const togglePermission = (moduleId, action) => {
    setRoleForm(prev => {
      const newPermissions = { ...prev.permissions };
      if (!newPermissions[moduleId]) {
        newPermissions[moduleId] = [];
      }
      
      const idx = newPermissions[moduleId].indexOf(action);
      if (idx > -1) {
        newPermissions[moduleId] = newPermissions[moduleId].filter(a => a !== action);
        if (newPermissions[moduleId].length === 0) {
          delete newPermissions[moduleId];
        }
      } else {
        newPermissions[moduleId] = [...newPermissions[moduleId], action];
      }
      
      return { ...prev, permissions: newPermissions };
    });
  };

  const toggleAllModulePermissions = (moduleId) => {
    setRoleForm(prev => {
      const newPermissions = { ...prev.permissions };
      const currentActions = newPermissions[moduleId] || [];
      
      if (currentActions.length === actions.length) {
        delete newPermissions[moduleId];
      } else {
        newPermissions[moduleId] = [...actions];
      }
      
      return { ...prev, permissions: newPermissions };
    });
  };

  const toggleAllPermissions = () => {
    setRoleForm(prev => {
      const totalPermissions = Object.keys(prev.permissions).length;
      if (totalPermissions === modules.length) {
        return { ...prev, permissions: {} };
      } else {
        const allPermissions = {};
        modules.forEach(m => {
          allPermissions[m.id] = [...actions];
        });
        return { ...prev, permissions: allPermissions };
      }
    });
  };

  const hasPermission = (moduleId, action) => {
    return roleForm.permissions[moduleId]?.includes(action) || false;
  };

  const getPermissionCount = (role) => {
    let count = 0;
    Object.values(role.permissions || {}).forEach(acts => {
      count += acts.length;
    });
    return count;
  };

  const renderPermissionMatrix = () => (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="bg-slate-50 p-3 border-b border-slate-200 flex items-center justify-between">
        <span className="text-sm font-bold text-slate-600 uppercase tracking-wider">Permission Matrix</span>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleAllPermissions}
          className="text-xs"
        >
          Toggle All
        </Button>
      </div>
      <ScrollArea className="h-[400px]">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 bg-slate-50">
              <TableHead className="text-slate-500 font-bold text-xs sticky left-0 bg-slate-50 min-w-[180px]">MODULE</TableHead>
              {actions.map(action => (
                <TableHead key={action} className="text-slate-500 font-bold text-xs text-center w-24">
                  {action.toUpperCase()}
                </TableHead>
              ))}
              <TableHead className="text-slate-500 font-bold text-xs text-center w-20">ALL</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {modules.map(module => {
              const isApprovals = module.id === 'approvals';
              const isLoans = module.id === 'loans';
              const isIE = module.id === 'income_expenses';
              const isTransactions = module.id === 'transactions';
              const hasApprovalsPerm = isApprovals && (roleForm.permissions['approvals']?.length || 0) > 0;
              const hasLoansPerm = isLoans && (roleForm.permissions['loans']?.length || 0) > 0;
              const hasIEPerm = isIE && (roleForm.permissions['income_expenses']?.length || 0) > 0;
              const hasTransactionsPerm = isTransactions && (roleForm.permissions['transactions']?.length || 0) > 0;
              const TRANSACTION_TYPES = ['deposit','withdrawal','transfer','commission','rebate','adjustment'];
              return (
                <Fragment key={module.id}>
                <TableRow className="border-slate-200 hover:bg-slate-50">
                  <TableCell className="font-medium text-slate-700 sticky left-0 bg-white">
                    {module.name}
                  </TableCell>
                  {actions.map(action => {
                    const Icon = ACTION_ICONS[action] || CheckCircle;
                    const isChecked = hasPermission(module.id, action);
                    return (
                      <TableCell key={action} className="text-center">
                        <button
                          onClick={() => togglePermission(module.id, action)}
                          className={`p-2 rounded-md transition-all ${
                            isChecked
                              ? ACTION_COLORS[action]
                              : 'bg-slate-100 text-slate-300 hover:bg-slate-200'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </button>
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center">
                    <Checkbox
                      checked={(roleForm.permissions[module.id]?.length || 0) === actions.length}
                      onCheckedChange={() => toggleAllModulePermissions(module.id)}
                    />
                  </TableCell>
                </TableRow>

                {/* Treasury account selector — shows under Pending Approvals when its perms are enabled */}
                {hasApprovalsPerm && treasuryAccounts.length > 0 && (
                  <TableRow key="treasury-accounts" className="bg-blue-50/40 border-slate-200">
                    <TableCell colSpan={actions.length + 2} className="py-2 px-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-blue-700 shrink-0">Allowed Accounts:</span>
                        <button
                          onClick={() => setRoleForm(prev => ({ ...prev, treasury_account_ids: null }))}
                          className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                            roleForm.treasury_account_ids === null
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-slate-500 border-slate-300 hover:border-blue-400'
                          }`}
                        >All</button>
                        {treasuryAccounts.map(acc => {
                          const selected = Array.isArray(roleForm.treasury_account_ids) &&
                            roleForm.treasury_account_ids.includes(acc.account_id);
                          return (
                            <button
                              key={acc.account_id}
                              onClick={() => {
                                setRoleForm(prev => {
                                  const current = Array.isArray(prev.treasury_account_ids) ? prev.treasury_account_ids : [];
                                  const next = selected
                                    ? current.filter(id => id !== acc.account_id)
                                    : [...current, acc.account_id];
                                  return { ...prev, treasury_account_ids: next.length ? next : null };
                                });
                              }}
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                                selected
                                  ? 'bg-blue-100 text-blue-700 border-blue-400'
                                  : 'bg-white text-slate-500 border-slate-300 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              {acc.account_name}
                              {acc.currency && <span className="ml-1 opacity-60">({acc.currency})</span>}
                            </button>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Loans borrower company selector — shows when loans perms are enabled */}
                {hasLoansPerm && borrowerCompanies.length > 0 && (
                  <TableRow key="loan-borrowers" className="bg-emerald-50/40 border-slate-200">
                    <TableCell colSpan={actions.length + 2} className="py-2 px-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-emerald-700 shrink-0">Allowed Borrower Companies:</span>
                        <button
                          onClick={() => setRoleForm(prev => ({ ...prev, borrower_ids: null }))}
                          className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                            roleForm.borrower_ids === null
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-slate-500 border-slate-300 hover:border-emerald-400'
                          }`}
                        >All</button>
                        {borrowerCompanies.map(company => {
                          const selected = Array.isArray(roleForm.borrower_ids) &&
                            roleForm.borrower_ids.includes(company.vendor_id);
                          return (
                            <button
                              key={company.vendor_id}
                              onClick={() => {
                                setRoleForm(prev => {
                                  const current = Array.isArray(prev.borrower_ids) ? prev.borrower_ids : [];
                                  const next = selected
                                    ? current.filter(id => id !== company.vendor_id)
                                    : [...current, company.vendor_id];
                                  return { ...prev, borrower_ids: next.length ? next : null };
                                });
                              }}
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                                selected
                                  ? 'bg-emerald-100 text-emerald-700 border-emerald-400'
                                  : 'bg-white text-slate-500 border-slate-300 hover:border-emerald-400 hover:text-emerald-600'
                              }`}
                            >
                              {company.name || company.vendor_name}
                              {company.loan_stats?.active_loans > 0 && (
                                <span className="ml-1 opacity-60">({company.loan_stats.active_loans} loans)</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Income & Expenses — own entries only toggle */}
                {hasIEPerm && (
                  <TableRow key="ie-own-only" className="bg-violet-50/40 border-slate-200">
                    <TableCell colSpan={actions.length + 2} className="py-2 px-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-violet-700 shrink-0">Data Entry Restriction:</span>
                        <button
                          onClick={() => setRoleForm(prev => ({ ...prev, ie_own_entries_only: !prev.ie_own_entries_only }))}
                          className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                            roleForm.ie_own_entries_only
                              ? 'bg-violet-600 text-white border-violet-600'
                              : 'bg-white text-slate-500 border-slate-300 hover:border-violet-400 hover:text-violet-600'
                          }`}
                        >
                          <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${roleForm.ie_own_entries_only ? 'border-white' : 'border-slate-400'}`}>
                            {roleForm.ie_own_entries_only && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
                          </span>
                          Own entries only — can only see &amp; access records they created
                        </button>
                        {!roleForm.ie_own_entries_only && (
                          <span className="text-xs text-slate-400">All entries visible</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Transactions — allowed transaction types selector */}
                {hasTransactionsPerm && (
                  <TableRow key="tx-types" className="bg-amber-50/40 border-slate-200">
                    <TableCell colSpan={actions.length + 2} className="py-2 px-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-amber-700 shrink-0">Allowed Types:</span>
                        <button
                          onClick={() => setRoleForm(prev => ({ ...prev, transaction_type_ids: null }))}
                          className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                            roleForm.transaction_type_ids === null
                              ? 'bg-amber-600 text-white border-amber-600'
                              : 'bg-white text-slate-500 border-slate-300 hover:border-amber-400'
                          }`}
                        >All</button>
                        {TRANSACTION_TYPES.map(type => {
                          const selected = Array.isArray(roleForm.transaction_type_ids) &&
                            roleForm.transaction_type_ids.includes(type);
                          return (
                            <button
                              key={type}
                              onClick={() => {
                                setRoleForm(prev => {
                                  const current = Array.isArray(prev.transaction_type_ids) ? prev.transaction_type_ids : [];
                                  const next = selected
                                    ? current.filter(t => t !== type)
                                    : [...current, type];
                                  return { ...prev, transaction_type_ids: next.length ? next : null };
                                });
                              }}
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium capitalize transition-colors ${
                                selected
                                  ? 'bg-amber-100 text-amber-700 border-amber-400'
                                  : 'bg-white text-slate-500 border-slate-300 hover:border-amber-400 hover:text-amber-600'
                              }`}
                            >{type}</button>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Transactions — allowed transaction tags selector */}
                {hasTransactionsPerm && clientTags.length > 0 && (
                  <TableRow key="tx-tags" className="bg-orange-50/40 border-slate-200">
                    <TableCell colSpan={actions.length + 2} className="py-2 px-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-orange-700 shrink-0">Allowed Client Tags:</span>
                        <button
                          onClick={() => setRoleForm(prev => ({ ...prev, allowed_client_tags: null }))}
                          className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                            roleForm.allowed_client_tags === null
                              ? 'bg-orange-600 text-white border-orange-600'
                              : 'bg-white text-slate-500 border-slate-300 hover:border-orange-400'
                          }`}
                        >All</button>
                        {clientTags.map(tag => {
                          const selected = Array.isArray(roleForm.allowed_client_tags) &&
                            roleForm.allowed_client_tags.includes(tag.tag_id);
                          return (
                            <button
                              key={tag.tag_id}
                              onClick={() => {
                                setRoleForm(prev => {
                                  const current = Array.isArray(prev.allowed_client_tags) ? prev.allowed_client_tags : [];
                                  const next = selected
                                    ? current.filter(id => id !== tag.tag_id)
                                    : [...current, tag.tag_id];
                                  return { ...prev, allowed_client_tags: next.length ? next : null };
                                });
                              }}
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                                selected
                                  ? 'bg-orange-100 text-orange-700 border-orange-400'
                                  : 'bg-white text-slate-500 border-slate-300 hover:border-orange-400 hover:text-orange-600'
                              }`}
                            >
                              {tag.color && <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: tag.color }} />}
                              {tag.name}
                            </button>
                          );
                        })}
                        {(() => {
                          const UNTAG = '__untagged__';
                          const selected = Array.isArray(roleForm.allowed_client_tags) && roleForm.allowed_client_tags.includes(UNTAG);
                          return (
                            <button
                              onClick={() => setRoleForm(prev => {
                                const current = Array.isArray(prev.allowed_client_tags) ? prev.allowed_client_tags : [];
                                const next = selected ? current.filter(id => id !== UNTAG) : [...current, UNTAG];
                                return { ...prev, allowed_client_tags: next.length ? next : null };
                              })}
                              title="Include transactions/clients that have no tag"
                              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                                selected
                                  ? 'bg-orange-100 text-orange-700 border-orange-400'
                                  : 'bg-white text-slate-500 border-slate-300 hover:border-orange-400 hover:text-orange-600'
                              }`}
                            >
                              <span className="inline-block w-2 h-2 rounded-full mr-1 bg-slate-400" />
                              Untagged
                            </button>
                          );
                        })()}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="roles-permissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Roles & Permissions
          </h1>
          <p className="text-slate-500">Manage user roles and granular access control</p>
        </div>
        <Button
          onClick={() => { resetForm(); setIsAddRoleOpen(true); }}
          className="bg-[#66FCF1] text-[#0B0C10] hover:bg-[#45A29E] font-bold uppercase tracking-wider rounded-sm glow-cyan"
          data-testid="add-role-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Role
        </Button>
      </div>

      {/* Main Content */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 mb-4">
          <TabsTrigger value="roles" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-600">
            <Shield className="w-4 h-4 mr-2" /> Roles
          </TabsTrigger>
          <TabsTrigger value="overview" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-600">
            <Lock className="w-4 h-4 mr-2" /> Permission Overview
          </TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#66FCF1] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid gap-4">
              {roles.map(role => (
                <Card key={role.role_id} className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${role.is_system_role ? 'bg-blue-100' : 'bg-slate-100'}`}>
                          <Shield className={`w-6 h-6 ${role.is_system_role ? 'text-blue-600' : 'text-slate-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-slate-800">{role.display_name}</h3>
                            {role.is_system_role && (
                              <Badge className="bg-blue-100 text-blue-600 border-blue-200 text-xs">System</Badge>
                            )}
                            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                              Level {role.hierarchy_level}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{role.description || 'No description'}</p>
                          <div className="flex items-center gap-4 mt-3">
                            <span className="text-xs text-slate-400">
                              <Users className="w-3 h-3 inline mr-1" />
                              {getPermissionCount(role)} permissions
                            </span>
                            <span className="text-xs text-slate-400">
                              Modules: {Object.keys(role.permissions || {}).length}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditRole(role)}
                        className="text-slate-600 hover:text-slate-800"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                    
                    {/* Permission preview */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider">Modules with access:</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(role.permissions || {}).slice(0, 8).map(([moduleId, acts]) => (
                          <Badge 
                            key={moduleId} 
                            variant="outline" 
                            className="text-xs bg-slate-50"
                          >
                            {modules.find(m => m.id === moduleId)?.name || moduleId}
                            <span className="ml-1 text-slate-400">({acts.length})</span>
                          </Badge>
                        ))}
                        {Object.keys(role.permissions || {}).length > 8 && (
                          <Badge variant="outline" className="text-xs bg-slate-50">
                            +{Object.keys(role.permissions).length - 8} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Permission Overview Tab */}
        <TabsContent value="overview">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-0">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-bold text-slate-700">Permission Matrix by Role</h3>
                <p className="text-sm text-slate-500">Overview of all permissions across roles</p>
              </div>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 bg-slate-50">
                      <TableHead className="text-slate-500 font-bold text-xs sticky left-0 bg-slate-50 min-w-[180px]">MODULE</TableHead>
                      {roles.map(role => (
                        <TableHead key={role.role_id} className="text-slate-500 font-bold text-xs text-center min-w-[100px]">
                          {role.display_name}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {modules.map(module => (
                      <TableRow key={module.id} className="border-slate-200">
                        <TableCell className="font-medium text-slate-700 sticky left-0 bg-white">
                          {module.name}
                        </TableCell>
                        {roles.map(role => {
                          const modulePerms = role.permissions?.[module.id] || [];
                          const permCount = modulePerms.length;
                          return (
                            <TableCell key={role.role_id} className="text-center">
                              {permCount > 0 ? (
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-slate-500">{permCount}</span>
                                </div>
                              ) : (
                                <XCircle className="w-4 h-4 text-slate-300 mx-auto" />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Role Dialog */}
      <Dialog open={isAddRoleOpen} onOpenChange={setIsAddRoleOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              Create New Role
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500 text-xs uppercase">Role Name (ID) *</Label>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="border-slate-200 mt-1"
                  placeholder="e.g., finance_manager"
                />
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase">Display Name *</Label>
                <Input
                  value={roleForm.display_name}
                  onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                  className="border-slate-200 mt-1"
                  placeholder="e.g., Finance Manager"
                />
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase">Hierarchy Level</Label>
                <Input
                  type="number"
                  value={roleForm.hierarchy_level}
                  onChange={(e) => setRoleForm({ ...roleForm, hierarchy_level: parseInt(e.target.value) || 0 })}
                  className="border-slate-200 mt-1"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-slate-400 mt-1">Higher = more access (0-100)</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase">Description</Label>
                <Textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="border-slate-200 mt-1"
                  placeholder="Describe this role's purpose..."
                  rows={2}
                />
              </div>
            </div>

            {renderPermissionMatrix()}

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setIsAddRoleOpen(false)} className="border-slate-200">
                Cancel
              </Button>
              <Button onClick={handleCreateRole} className="bg-[#66FCF1] text-[#0B0C10] hover:bg-[#45A29E]">
                Create Role
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditRoleOpen} onOpenChange={setIsEditRoleOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              Edit Role: {selectedRole?.display_name}
              {selectedRole?.is_system_role && (
                <Badge className="ml-2 bg-blue-100 text-blue-600">System Role</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500 text-xs uppercase">Role Name (ID)</Label>
                <Input
                  value={roleForm.name}
                  disabled
                  className="border-slate-200 mt-1 bg-slate-50"
                />
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase">Display Name *</Label>
                <Input
                  value={roleForm.display_name}
                  onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                  className="border-slate-200 mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase">Hierarchy Level</Label>
                <Input
                  type="number"
                  value={roleForm.hierarchy_level}
                  onChange={(e) => setRoleForm({ ...roleForm, hierarchy_level: parseInt(e.target.value) || 0 })}
                  className="border-slate-200 mt-1"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <Label className="text-slate-500 text-xs uppercase">Description</Label>
                <Textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="border-slate-200 mt-1"
                  rows={2}
                />
              </div>
            </div>

            {renderPermissionMatrix()}

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setIsEditRoleOpen(false)} className="border-slate-200">
                Cancel
              </Button>
              <Button onClick={handleUpdateRole} className="bg-[#66FCF1] text-[#0B0C10] hover:bg-[#45A29E]">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
